'use client'

import { Card } from '@/components/ui/card'
import { Bell, Users, Clock, Volume2, Lock, Smartphone } from 'lucide-react'

const features = [
  {
    icon: Bell,
    title: 'Full-Screen Alarms',
    description: 'Attention-grabbing reminders with sound, vibration, and full-screen alerts designed to never be missed.',
    color: 'from-primary',
  },
  {
    icon: Volume2,
    title: 'Voice Reminders',
    description: 'Smart text-to-speech alerts that speak medicine names and instructions aloud for better accessibility.',
    color: 'from-secondary',
  },
  {
    icon: Users,
    title: 'Caregiver Support',
    description: 'Remote medicine management for family members and caregivers to help loved ones stay on track.',
    color: 'from-accent',
  },
  {
    icon: Clock,
    title: 'Smart Scheduling',
    description: 'Repeating schedules, custom timings, and personalized reminders set up in just a few taps.',
    color: 'from-primary',
  },
  {
    icon: Lock,
    title: 'Secure Sync',
    description: 'Cloud-backed data synchronization with enterprise-grade security for peace of mind.',
    color: 'from-secondary',
  },
  {
    icon: Smartphone,
    title: 'Built for Everyone',
    description: 'Clean, intuitive interface designed specifically for seniors, caregivers, and daily medication users.',
    color: 'from-accent',
  },
]

export function Features() {
  return (
    <section id="features" className="w-full py-20 md:py-28 bg-muted/30">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col gap-16">
          <div className="space-y-4">
            <p className="text-sm font-bold text-secondary uppercase tracking-wider">Core Features</p>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground text-balance">
              Everything you need for medicine management
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Alarvia combines powerful features with simplicity, making medication management reliable and stress-free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <Card key={index} className="p-8 border-border/50 hover:shadow-xl hover:border-primary/50 transition-all group overflow-hidden">
                  <div className="absolute -right-16 -top-16 w-32 h-32 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity rounded-full blur-3xl" style={{backgroundImage: `linear-gradient(to bottom right, var(--primary), var(--accent))`}}></div>
                  
                  <div className="relative flex flex-col gap-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} to-transparent opacity-80 flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                </Card>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
